// src/pages/MegaMenu.jsx
import React, { useState, useMemo, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  FaCalendarAlt,
  FaEnvelope,
  FaCalendarPlus,
  FaMapMarked,
  FaAddressCard,
  FaImage,
  FaUserTie,
  FaCalendarCheck,
  FaYoutube,
  FaBell,
} from "react-icons/fa";
import { IoMdSettings } from "react-icons/io";
import { GiKimono, GiGong } from "react-icons/gi";
import {
  MdOutlineInventory,
  MdGroups,
  MdEmojiEvents,
  MdEventNote,
} from "react-icons/md";
import {
  PiTranslateFill,
  PiExamFill,
  PiUserListFill,
  PiArticleNyTimesFill,
} from "react-icons/pi";
import { MdScoreboard } from "react-icons/md";
import { IoNewspaper } from "react-icons/io5";
import { LuListTodo } from "react-icons/lu";
import { BsDiagram3Fill } from "react-icons/bs";
import { RiArticleFill } from "react-icons/ri";
import { ImUsers } from "react-icons/im";
import { SiGoogleforms } from "react-icons/si";
import { useUser } from "./../../contexts/UserContext";

const linkIcon = {
  Inscription: <SiGoogleforms />,
  Calendrier: <FaCalendarAlt />,
  Résultats: <MdEmojiEvents />,
  Actualités: <PiArticleNyTimesFill />,
  Newsletter: <IoNewspaper />,
  "Galerie Photos": <FaImage />,
  Commissions: <BsDiagram3Fill />,
  "Listing Clubs": <FaMapMarked />,
  "Vie des Clubs": <RiArticleFill />,
  Lexique: <PiTranslateFill />,
  "Arbitrage Judo": <GiGong />,
  // "Judo Score": <MdScoreboard />,
  Techniques: <GiKimono />,
  "Partenaires Kata": <ImUsers />,
  Messagerie: <FaEnvelope />,
  Notifications: <FaBell />,
  "Petites Annonces": <FaAddressCard />,
  "Proposer événement": <MdEventNote />,
  Correspondance: <PiArticleNyTimesFill />,
  ToDoList: <LuListTodo />,
  Convocations: <FaCalendarCheck />,
  "Règlement Arbitrage": <GiGong />,
  "Quizz Video": <FaYoutube />,
  "Listing utilisateurs": <PiUserListFill />,
  "Listing acteurs": <GiKimono />,
  "Listing groupes": <MdGroups />,
  "Listing Officiels": <FaUserTie />,
  "Convocations Admin": <FaCalendarCheck />,
  Notation: <PiExamFill />,
  Événements: <FaCalendarPlus />,
  Inventaire: <MdOutlineInventory />,
  Paramètres: <IoMdSettings />,
};

const sections = {
  1: {
    label: "Visiteurs & Utilisateurs",
    links: [
      "Inscription",
      "Calendrier",
      "Résultats",
      "Actualités",
      "Newsletter",
      "Galerie Photos",
      "Commissions",
      "Listing Clubs",
      "Vie des Clubs",
      "Lexique",
      "Arbitrage Judo",
      // "Judo Score",
      "Techniques",
      "Partenaires Kata",
    ],
  },
  2: {
    label: "Responsables Clubs & Enseignants",
    links: ["Messagerie", "Petites Annonces", "Proposer événement", "Correspondance"],
  },
  3: {
    label: "Membres du Comité",
    links: ["Messagerie", "Convocations", "ToDoList"],
  },
  4: {
    label: "Arbitres & Commissaires Sportifs",
    links: ["Messagerie", "Convocations", "Règlement Arbitrage", "Quizz Video"],
  },
  5: {
    label: "Administrateur, CTF, Secrétaire",
    links: [
      "Messagerie",
      "Notifications",
      "Listing utilisateurs",
      "Listing acteurs",
      "Listing groupes",
      "Listing Officiels",
      "Convocations Admin",
      "Notation",
      "Événements",
      "Inventaire",
      "Paramètres",
    ],
  },
};

// même slug que celui utilisé dans le NavLink
const slugify = (label) =>
  label
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // accents
    .replace(/['’]/g, "") // apostrophes
    .replace(/ç/g, "c")
    .replace(/[\s]+/g, "-");

const MegaMenu = ({ role_id, onClose }) => {
  const { user } = useUser();
  const location = useLocation();

  // Sections ordonnées + filtrage de "Inscription" si pas connecté
  const orderedSections = useMemo(() => {
    let base;

    if (role_id === 7) {
      base = [sections[1], sections[2], sections[3], sections[4]];
    } else if (role_id === 6) {
      base = [sections[1]];
    } else if (role_id === 5) {
      base = [sections[1], sections[2]];
    } else if (role_id === 3) {
      base = [sections[1], sections[3]];
    } else if (role_id === 4) {
      base = [sections[1], sections[4]];
    } else if (role_id === 1 || role_id === 2) {
      base = [sections[1], sections[2], sections[3], sections[4], sections[5]];
    } else {
      base = [sections[1]];
    }

    return base.map((section) => ({
      ...section,
      links: section.links.filter((link) => {
        if (link === "Inscription" && !user) return false;
        return true;
      }),
    }));
  }, [role_id, user]);

  const [activeIndex, setActiveIndex] = useState(0);

  // Choix de l'onglet par défaut :
  // 1) en fonction de l'URL courante
  // 2) sinon, à partir de localStorage
  useEffect(() => {
    if (!orderedSections.length) return;

    const currentSlug = location.pathname.split("/")[1] || "";

    let indexFromPath = orderedSections.findIndex((section) =>
      section.links.some((link) => slugify(link) === currentSlug)
    );

    if (indexFromPath === -1) {
      try {
        const stored = localStorage.getItem("megaMenuLastSection");
        const storedIdx = parseInt(stored, 10);
        if (
          !Number.isNaN(storedIdx) &&
          storedIdx >= 0 &&
          storedIdx < orderedSections.length
        ) {
          indexFromPath = storedIdx;
        } else {
          indexFromPath = 0;
        }
      } catch {
        indexFromPath = 0;
      }
    }

    setActiveIndex(indexFromPath);
  }, [location.pathname, orderedSections]);

  // Sauvegarde de l’onglet actif
  useEffect(() => {
    try {
      localStorage.setItem("megaMenuLastSection", String(activeIndex));
    } catch {
      // silencieux si localStorage indisponible
    }
  }, [activeIndex]);

  const activeSection =
    orderedSections[activeIndex] || orderedSections[0] || { links: [], label: "" };

  return (
    <div className="max-w-[1280px] mx-auto rounded-2xl bg-gradient-to-br from-slate-500 via-slate-700 to-slate-900 text-white font-montserrat overflow-hidden relative shadow-2xl border border-white/10">
      {/* Bouton de fermeture */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white focus:outline-none"
        style={{ zIndex: 10 }}
      >
        <span className="sr-only">Fermer le menu</span>×
      </button>

      <div className="px-6 md:px-8 py-6 md:py-8">
        {/* Titre général */}
        <p className="text-[11px] uppercase tracking-[0.3em] text-primary/80 mb-1">
          NAVIGATION DANS L&apos;APPLICATION
        </p>
        <h2 className="text-lg md:text-xl font-semibold mb-4">
          Accès rapide à vos espaces
        </h2>

        {/* Onglets rôles : plus d’ascenseur horizontal, on laisse wrap */}
        <div className="flex flex-wrap gap-2 pb-3">
          {orderedSections.map((section, idx) => (
            <button
              key={section.label}
              onClick={() => setActiveIndex(idx)}
              className={`px-3 py-1.5 md:px-4 md:py-2 rounded-full text-xs md:text-sm whitespace-nowrap border transition-all ${
                idx === activeIndex
                  ? "bg-primary text-white border-primary shadow-md"
                  : "bg-slate-900/70 text-slate-200 border-white/10 hover:bg-slate-800/90"
              }`}
            >
              {section.label}
            </button>
          ))}
        </div>

        {/* Liens de la section active */}
        <div className="mt-5">
          <div className="text-sm text-slate-300 mb-3">
            {activeSection.label}
          </div>
          <ul className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
            {activeSection.links.map((link) => (
              <li key={link}>
                <NavLink
                  to={`/${slugify(link)}`}
                  className="group block relative overflow-hidden rounded-xl p-4 shadow-lg bg-slate-900/60 hover:bg-slate-800/80 border border-white/10 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-2xl"
                  onClick={onClose}
                >
                  <div className="flex flex-col items-center justify-center gap-3">
                    <div className="text-3xl text-primary group-hover:scale-110 transition-transform duration-300">
                      {linkIcon[link]}
                    </div>
                    <span className="block text-sm font-semibold text-slate-50 text-center">
                      {link}
                    </span>
                  </div>
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default MegaMenu;
